import React, { useEffect, useRef, useState } from 'react';
import {
  FiUsers,
  FiUserPlus,
  FiAward,
  FiRefreshCw
} from 'react-icons/fi';
import Chart from 'chart.js/auto';
import './HomePage.css';

const url = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const chartRef = useRef(null);
  const monthlyChartRef = useRef(null);

  // Safe fetch
  const safeFetchJson = async (fetchUrl, headers = {}) => {
    try {
      const res = await fetch(fetchUrl, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      console.warn(fetchUrl, err.message);
      return null;
    }
  };

  // Fetch dashboard stats
  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [
        houseData,
        collectorData,
        customerData,
        paymentData
      ] = await Promise.all([
        safeFetchJson(`${url}/api/housetype/stats`, headers),
        safeFetchJson(`${url}/api/collectors/stats/count`, headers),
        safeFetchJson(`${url}/api/customer/stats`, headers),
        safeFetchJson(`${url}/api/payment/stats`, headers),
      ]);

      setStats({
        houses: houseData?.totalHouses ?? 0,
        collectors: collectorData?.totalCollectors ?? 0,
        customers: {
          total: customerData?.totalCustomers ?? 0,
          newThisMonth: customerData?.newThisMonth ?? 0,
        },
        payments: {
          total: paymentData?.totalPayments ?? 0,
          newThisMonth: paymentData?.newThisMonth ?? 0,
        },
        memberChart: customerData?.customerChart ?? Array(12).fill(0),
        paymentChart: paymentData?.paymentChart ?? Array(12).fill(0),
        lastUpdated: new Date(),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 300000);
    return () => clearInterval(interval);
  }, []);

  // Charts
  useEffect(() => {
    if (!chartRef.current || !monthlyChartRef.current) return;

    if (chartRef.current.chart) chartRef.current.chart.destroy();
    if (monthlyChartRef.current.chart) monthlyChartRef.current.chart.destroy();

    chartRef.current.chart = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        datasets: [{
          label: 'Customers',
          data: stats.memberChart || [],
          borderColor: '#10B981',
          backgroundColor: 'rgba(16,185,129,0.15)',
          fill: true,
          tension: 0.4,
        }],
      },
      options: { responsive: true }
    });

    monthlyChartRef.current.chart = new Chart(monthlyChartRef.current, {
      type: 'bar',
      data: {
        labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        datasets: [
          {
            label: 'Customers',
            data: stats.memberChart || [],
            backgroundColor: '#10B981',
          },
          {
            label: 'Payments',
            data: stats.paymentChart || [],
            backgroundColor: '#F43F5E',
          },
        ],
      },
      options: { responsive: true }
    });
  }, [stats]);

  return (
    <div className="home-container">
      {/* HEADER */}
      <header className="home-header">
        <h1 className="welcome-title">
          Deegan <span className="brand">Wadaag Waste Management</span>
        </h1>

        <button className="refresh-btn" onClick={fetchStats} disabled={loading}>
          <FiRefreshCw className={loading ? 'spinning' : ''} />
          {loading ? ' Loading...' : ' Refresh'}
        </button>
      </header>

      {error && <div className="error-alert">Error: {error}</div>}

      {/* STATS CARDS */}
      <section className="stats-grid">
  <div className="stat-card">
    <div className="stat-icon">
      <FiUsers />
    </div>
    <p className="stat-label">Total Houses</p>
    <h3 className="stat-value">{stats.houses}</h3>
  </div>

  <div className="stat-card">
    <div className="stat-icon">
      <FiUserPlus />
    </div>
    <p className="stat-label">Total Collectors</p>
    <h3 className="stat-value">{stats.collectors}</h3>
  </div>

  <div className="stat-card">
    <div className="stat-icon">
      <FiUserPlus />
    </div>
    <p className="stat-label">Total Customers</p>
    <h3 className="stat-value">{stats.customers?.total}</h3>
  </div>

  <div className="stat-card">
    <div className="stat-icon">
      <FiAward />
    </div>
    <p className="stat-label">New Customers This Month</p>
    <h3 className="stat-value">{stats.customers?.newThisMonth}</h3>
  </div>

  <div className="stat-card">
    <div className="stat-icon">
      <FiUserPlus />
    </div>
    <p className="stat-label">Total Payments</p>
    <h3 className="stat-value">{stats.payments?.total}</h3>
  </div>

  <div className="stat-card">
    <div className="stat-icon">
      <FiAward />
    </div>
    <p className="stat-label">New Payments This Month</p>
    <h3 className="stat-value">{stats.payments?.newThisMonth}</h3>
  </div>
</section>


      {/* CHARTS */}
      <section className="charts-section">
        <div className="chart-container">
          <canvas ref={chartRef}></canvas>
        </div>

        <div className="chart-container">
          <canvas ref={monthlyChartRef}></canvas>
        </div>
      </section>
    </div>
  );
};

export default Home;
